<template>
  <aside
    class="elevation-panel"
    :class="{ 'collapsed': isCollapsed }"
    :aria-labelledby="headingId"
    role="complementary"
  >
    <!-- chart content -->
    <div class="chart-container" v-show="!isCollapsed">
      <div class="chart-hint">
        <span
          class="material-icons"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          :data-bs-title="$t('plan_trail.elevation.hover_click_hint')"
        >
          info
        </span>
        <span v-if="activeRouteName" class="route-name-label">{{ activeRouteName }}</span>
        <button
          class="icon-btn"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          data-bs-custom-class="weather-tooltip"
          :data-bs-title="$t('plan_trail.elevation.hide_elevation')"
          :aria-label="$t('plan_trail.elevation.collapse_elevation_panel')"
          @click="toggleCollapse"
          >
          <span class="material-icons">bottom_panel_close</span>
        </button>
        <small></small>
      </div>
      <div id="line-chart" class="elevation-chart">
        <canvas id="elevationChart"></canvas>
      </div>
    </div>
  </aside>

  <!-- Expand button when collapsed -->
  <button
    v-if="isCollapsed"
    class="expand-button"
    @click="toggleCollapse"
    :aria-label="$t('plan_trail.elevation.expand_elevation_panel')"
  >
    <span class="material-icons">bottom_panel_open</span>
    <span>{{ $t('shared.elevation') }}</span>
  </button>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onUnmounted, shallowRef, nextTick } from 'vue';
import { useStore } from 'vuex';
import { i18n } from '../../../i18n';
import { Tooltip as BootstrapTooltip } from 'bootstrap';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';
import { SURFACE_COLORS, surfaceColor } from '../../../utils/surfaceColors';
// turf imports removed — hover now uses pre-computed chart data instead of per-hover calculations
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
  Plugin,
} from 'chart.js';
import mapboxgl from 'mapbox-gl';
import { ServiceProvider } from '../services/ServiceProvider';
import { MarkerFeature } from '../../../utils/models/MarkerFeature';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
);

// Store hover index globally to access in plugin
let currentHoverIndex: number | null = null;


// Custom plugin to draw vertical line
// ────────────────────────────────────────────────────────────────────
// Vertical line that spans the whole plotting area
// ────────────────────────────────────────────────────────────────────
const verticalLinePlugin: Plugin = {
  id: 'verticalLine',
  // Draw before the tooltip so the tooltip appears on top
  beforeTooltipDraw: (chart) => {
    // Bail early if nothing is being hovered
    if (currentHoverIndex === null) return;

    const xScale = chart.scales.x;                 // x-axis scale
    const { top, bottom } = chart.chartArea;       // plot area bounds
    // On a linear axis getPixelForValue() expects the x *value* (distance),
    // not the array index — pull the distance off the hovered {x, y} point.
    const point: any = chart.data.datasets?.[0]?.data?.[currentHoverIndex];
    const xValue = point && typeof point === 'object' ? point.x : currentHoverIndex;
    const x = xScale.getPixelForValue(xValue);

    if (Number.isNaN(x)) return;

    const ctx = chart.ctx;
    ctx.save();

    // 1. white “halo” to lift line off the grid
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom + 7);
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.stroke();

    // 2. slim grey line inside the halo
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    ctx.restore();
  },
};

/* NEW plugin – fires every time Chart.js gets a DOM event              */
/* It will see the real native `mouseout` / `mouseleave` and clean up.   */
const mouseLeavePlugin: Plugin = {
  id: 'mouseLeave',
  afterEvent(chart, args) {
    const t = args.event.type;
    if (t === 'mouseout' || t === 'mouseleave') {
      currentHoverIndex = null;
      onChartMouseLeave?.();
      chart.update('none');
    }
  }
};

Chart.register(verticalLinePlugin, mouseLeavePlugin);

let isHoveringChart = false;
let fallbackHoverMarker: mapboxgl.Marker | null = null;
let onChartMouseLeave: (() => void) | null = null;

// Surface names mapping. Getters so each name is translated when read, in the
// page's locale, rather than once at module load.
const surfaceNames: Record<string, string> = {
  get unknown() { return i18n.global.t('plan_trail.elevation.surface_unknown'); },
  get missing() { return i18n.global.t('plan_trail.elevation.surface_unknown'); },
  get paved() { return i18n.global.t('plan_trail.elevation.surface_paved'); },
  get unpaved() { return i18n.global.t('plan_trail.elevation.surface_unpaved'); },
  get asphalt() { return i18n.global.t('plan_trail.elevation.surface_asphalt'); },
  get concrete() { return i18n.global.t('plan_trail.elevation.surface_concrete'); },
  get cobblestone() { return i18n.global.t('plan_trail.elevation.surface_cobblestone'); },
  get metal() { return i18n.global.t('plan_trail.elevation.surface_metal'); },
  get wood() { return i18n.global.t('plan_trail.elevation.surface_wood'); },
  get compactedGravel() { return i18n.global.t('plan_trail.elevation.surface_compacted_gravel'); },
  get fineGravel() { return i18n.global.t('plan_trail.elevation.surface_fine_gravel'); },
  get gravel() { return i18n.global.t('plan_trail.elevation.surface_gravel'); },
  get dirt() { return i18n.global.t('plan_trail.elevation.surface_dirt'); },
  get ground() { return i18n.global.t('plan_trail.elevation.surface_ground'); },
  get ice() { return i18n.global.t('plan_trail.elevation.surface_ice'); },
  get pavingStones() { return i18n.global.t('plan_trail.elevation.surface_paving_stones'); },
  get sand() { return i18n.global.t('plan_trail.elevation.surface_sand'); },
  get woodchips() { return i18n.global.t('plan_trail.elevation.surface_woodchips'); },
  get grass() { return i18n.global.t('plan_trail.elevation.surface_grass'); },
  get grassPaver() { return i18n.global.t('plan_trail.elevation.surface_grass_paver'); },
};

export default defineComponent({
  name: 'PlanTrailElevation',
  setup() {
    const store = useStore();
    const headingId = `elevation-heading-${Math.random().toString(36).slice(2)}`;
    const isCollapsed = ref(false);
    const chart = shallowRef<Chart | null>(null);

    // Computed properties
    const chartData = computed(() => store.getters['planTrail/createChartData']);
    const mapHoverDistance = computed(() => store.state.planTrail.mapHoverDistance);
    const activeRouteName = computed(() => {
      const activeRoute = store.getters['planTrail/activeRoute'];
      return activeRoute?.name || null;
    });
    
    // Compute active surfaces for legend
    const activeSurfaces = computed(() => {
      if (!chartData.value || !chartData.value.surfaces) return [];
      const uniqueSurfaces = [...new Set(chartData.value.surfaces)];
      return uniqueSurfaces.sort((a, b) => a - b);
    });
    
    const showSurfaceLegend = computed(() => {
      return activeSurfaces.value.length > 0 && activeSurfaces.value.length <= 6;
    });

    // Methods
    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
      // Resize chart when expanding
      if (!isCollapsed.value && chart.value) {
        setTimeout(() => {
          chart.value?.resize();
        }, 300); // Wait for transition
      }
    };

    const createFallbackHoverMarkerEl = () => {
      const el = document.createElement('div');
      el.style.width = '12px';
      el.style.height = '12px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = '#bbbbbb';
      el.style.border = '2px solid #ffffff';
      return el;
    };

    const ensureHoverMarker = (lng: number, lat: number) => {
      try {
        const services = ServiceProvider.getInstance().getServices();
        const { mapService } = services;

        if (mapService.hasSource('hover-marker')) {
          mapService.updateMarkerPosition('hover-marker', [lng, lat]);
          return;
        }

        MarkerFeature.fromCoordinates([lng, lat], true)
          .then((markerFeature) => mapService.addMarker('hover-marker', markerFeature, {
            isActive: true,
            opacity: 1
          }))
          .catch(() => {});
        return;
      } catch (_) {
        if (window.map) {
          if (!fallbackHoverMarker) {
            fallbackHoverMarker = new mapboxgl.Marker({ element: createFallbackHoverMarkerEl() });
          }
          fallbackHoverMarker.setLngLat([lng, lat]).addTo(window.map);
        }
      }
    };

    const removeHoverMarker = () => {
      try {
        const services = ServiceProvider.getInstance().getServices();
        services.mapService.removeMarker('hover-marker');
      } catch (_) {
        if (fallbackHoverMarker) {
          fallbackHoverMarker.remove();
          fallbackHoverMarker = null;
        }
      }
    };

    const clearHoverState = () => {
      isHoveringChart = false;
      currentHoverIndex = null;
      store.commit('planTrail/setMapHoverDistance', null);
      store.commit('planTrail/setNearLine', false);
      store.commit('planTrail/setHoverPoint', null);
      removeHoverMarker();
      if (chart.value) {
        const tooltip = chart.value.tooltip;
        tooltip?.setActiveElements([]);
        chart.value.update('none');
      }
    };

    const createOptions = (hoverX: number | null = null): any => {
      const m = MeasurementPrefUtility.preference();

    return {
      interaction: {
        mode: 'nearest',
        intersect: false,
        axis: 'x',
      },
        onHover: (event, chartElements) => {
          if (!chart.value) return;

          if (chartElements.length && chart.value) {
            isHoveringChart = true;
            const index = chartElements[0].index;
            currentHoverIndex = index;

            const lat = chart.value.data.lat?.[index];
            const lon = chart.value.data.lon?.[index];

            const activeRoute = store.getters['planTrail/activeRoute'];
            const chartCoordMap = chart.value?.data?.coordToLineString;

            if (lat !== undefined && lon !== undefined && activeRoute && chartCoordMap && chartCoordMap[index]) {
              // Use the chart's own coordinate mapping — O(1) lookup instead of searching all linestrings
              const { lineStringIndex: nearestLineIndex } = chartCoordMap[index];
              const lineString = activeRoute.lineStrings[nearestLineIndex];

              if (lineString?.lineStringGeometry) {
                // Use pre-computed raw distance from chart data — O(1) lookup
                const distanceMeters = chart.value?.data?.distances?.[index] ?? 0;

                const lineStringId = lineString?.lineStringProperties?.id;
                const hoverCoordinates: [number, number] = [lon, lat];

                store.commit('planTrail/setMapHoverDistance', distanceMeters);
                store.commit('planTrail/setNearLine', true);
                if (lineStringId) {
                  store.commit('planTrail/setHoverPoint', {
                    lng: hoverCoordinates[0],
                    lat: hoverCoordinates[1],
                    lineStringId,
                    insertIndex: nearestLineIndex + 1
                  });
                } else {
                  store.commit('planTrail/setHoverPoint', null);
                }

                ensureHoverMarker(hoverCoordinates[0], hoverCoordinates[1]);
              } else {
                clearHoverState();
              }
            } else {
              clearHoverState();
            }

            chart.value.update('none');
          } else {
            clearHoverState();
          }
        },
        onClick: (event, chartElements, chartInstance) => {
          if (chartElements.length && chart.value) {
            const index = chartElements[0].index;
            const lat = chart.value.data.lat?.[index];
            const lon = chart.value.data.lon?.[index];
            const coordToLineString = chart.value.data.coordToLineString;

            if (lat !== undefined && lon !== undefined && coordToLineString && coordToLineString[index]) {
              // Find the corresponding route segment
              const activeRoute = store.getters['planTrail/activeRoute'];
              if (!activeRoute || !activeRoute.lineStrings.length) return;

              // Use the coordToLineString mapping to get the exact linestring and coord index
              const { lineStringIndex, coordIndex } = coordToLineString[index];

              // The insertIndex for the marker array is lineStringIndex + 1
              // because linestring[i] connects marker[i] to marker[i+1]
              // and we want to insert a new marker after marker[i]
              const insertIndex = lineStringIndex + 1;

              // The segmentIndex is the coordIndex within that linestring
              // because we're clicking on coordinate[coordIndex], which is part of
              // the segment from coordinate[coordIndex] to coordinate[coordIndex+1]
              const segmentIndex = coordIndex;

              // Dispatch action to insert marker
              store.dispatch('planTrail/insertMarkerAtPoint', {
                point: [lon, lat],
                routeId: activeRoute.id,
                insertIndex: insertIndex,
                segmentIndex: segmentIndex
              });
            }
          }
        },

        layout: { padding: { left: 10, right: 15, top: 15, bottom: 5 } },
        responsive: true,
        maintainAspectRatio: false,
        elements: { 
          point: { 
            radius: 0,
            hoverRadius: 4,
          },
          line: {
            tension: 0.1, // Slight curve for smoother appearance
          }
        },

        scales: {
          x: {
            // Linear distance axis: points are positioned by their real cumulative
            // distance (the {x, y} data carries distance in the user's big unit),
            // NOT by array index. A category axis spaced every point equally, which
            // crushed long straight segments into near-vertical "steps".
            type: 'linear',
            min: 0,
            grid: { display: true, color: 'rgba(134,134,134,0.2)' },
            ticks: {
              color: 'rgba(255,255,255,0.87)',
              font: { size: 10 },
              maxTicksLimit: 6,     // Adjust this number to show more or fewer ticks
              maxRotation: 0,       // Prevents labels from rotating
              minRotation: 0,       // Prevents labels from rotating
              callback: (v: number | string) => `${v} ${m.kilometerSymbol()}`,
            },
          },
          y: {
            grid: { display: true, color: 'rgba(134,134,134,0.2)' },
            ticks: {
              color: 'rgba(255,255,255,0.87)',
              font: { size: 10 },
              callback: (v: number | string) => `${v} ${m.meterSymbol()}`,
            },
          },
        },

        plugins: {
          legend: { display: false },
          tooltip: {
            intersect: false,
            callbacks: {
              label: (ctx) => {
                const surfaces = ctx.chart.data.surfaces;
                const surfaceType = surfaces ? surfaces[ctx.dataIndex] : null;
                const elevation = `${ctx.parsed.y} ${m.meterSymbol()}`;
                
                if (surfaceType && surfaceType !== 'missing' && surfaceType !== 'unknown') {
                  return elevation;
                }
                return elevation;
              },
              afterLabel: (ctx) => {
                const surfaces = ctx.chart.data.surfaces;
                const surfaceType = surfaces ? surfaces[ctx.dataIndex] : null;
                
                if (surfaceType && surfaceType !== 'missing' && surfaceType !== 'unknown') {
                  const surfaceName = surfaceNames[surfaceType] || surfaceType;
                  return i18n.global.t('plan_trail.elevation.surface_label', { surface: surfaceName });
                }
                return null;
              },
              labelColor: (ctx) => {
                const surfaces = ctx.chart.data.surfaces;
                const surfaceType = surfaces ? surfaces[ctx.dataIndex] : null;
                const color = surfaceColor(surfaceType);
                
                return {
                  borderColor: color,
                  backgroundColor: color,
                  borderWidth: 2,
                  borderRadius: 2,
                };
              },
            },
          },
          verticalLine: {}
        },

        animation: false,
      };
    };

    const updateChartData = async (data: any) => {
      if (data) {
        // Pin the linear x-axis to the route's actual end distance, otherwise
        // Chart.js rounds the max up to a "nice" value (e.g. 6 km for a 5.27 km
        // route) and leaves an empty stretch of profile at the end.
        const points = data.datasets?.[0]?.data;
        const lastX = points && points.length ? points[points.length - 1].x : undefined;
        if (chart.value != null) {
          chart.value.data = data;
          if (chart.value.options?.scales?.x) {
            (chart.value.options.scales.x as any).max = lastX;
          }
          chart.value.update();
        } else {
          const options = createOptions();
          if (lastX !== undefined) options.scales.x.max = lastX;
          const config = {
            type: 'line' as const,
            data: data,
            options,
          };
          const el = document.getElementById('elevationChart') as HTMLCanvasElement;
          if (el) {
            // Destroy any existing chart instance on this canvas
            const existingChart = Chart.getChart(el);
            if (existingChart) {
              existingChart.destroy();
            }
            chart.value = new Chart(el, config);
          }
        }
      }
    };

    // Watchers
    watch(chartData, async (newValue) => {
      await updateChartData(newValue);
    });

    watch(mapHoverDistance, (newDistance) => {
      if (isHoveringChart) return;

      if (!chart.value) return;

      let closestIndex: number | null = null;

      if (newDistance !== null && newDistance !== undefined && chartData.value) {
        // Convert distance to the value that would appear on X-axis
        const m = MeasurementPrefUtility.preference();
        const distanceInPreferredUnits = m.bigUnitValue(newDistance / 1000); // Convert meters to km/miles

        // Find the closest data point index for tooltip
        const xLabels = chartData.value.xLabels || [];
        let minDiff = Infinity;
        for (let i = 0; i < xLabels.length; i++) {
          const labelValue = parseFloat(xLabels[i]);
          const diff = Math.abs(labelValue - distanceInPreferredUnits);
          if (diff < minDiff) {
            minDiff = diff;
            closestIndex = i;
          }
        }

        // Set the current hover index for the vertical line
        currentHoverIndex = closestIndex;
      } else {
        currentHoverIndex = null;
      }

      // Trigger tooltip programmatically if not hovering on chart
      // if (!isHoveringChart && closestIndex !== null && newDistance !== null) {
      if (closestIndex !== null) {
        const tooltip = chart.value.tooltip;
        if (tooltip) {
          tooltip.setActiveElements([{
            datasetIndex: 0,
            index: closestIndex
          }]);
        }
      } else if (newDistance === null) {
        // Clear tooltip when not hovering
        const tooltip = chart.value.tooltip;
        if (tooltip) {
          tooltip.setActiveElements([]);
        }
      }

      // Single update call after setting both vertical line and tooltip
      chart.value.update('none');
    });

    // Lifecycle
    onMounted(() => {
      onChartMouseLeave = () => {
        clearHoverState();
      };

      nextTick(() => {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        [...tooltipTriggerList].map(tooltipTriggerEl => new BootstrapTooltip(tooltipTriggerEl));
      });

      setTimeout(() => {
        const data = store.getters['planTrail/createChartData'];
        updateChartData(data);
      }, 1000);
    });
    
    onUnmounted(() => {
      // Clean up chart instance when component is unmounted
      if (chart.value) {
        chart.value.destroy();
        chart.value = null;
      }
      removeHoverMarker();
      onChartMouseLeave = null;
      isHoveringChart = false;
    });

    return {
      headingId,
      isCollapsed,
      toggleCollapse,
      activeSurfaces,
      showSurfaceLegend,
      surfaceColors: SURFACE_COLORS,
      surfaceNames,
      activeRouteName,
    };
  },
});
</script>

<style scoped>
/* panel container */
.elevation-panel {
  padding: 0.5rem;
  position: absolute;
  bottom: 3.2rem;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  max-width: calc(100vw - 680px); /* Account for side panels */
  background: rgb(27 27 29 / 90%);
  color: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  z-index: 500;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

/* header */

.icon-btn {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 0;
}

.icon-btn:hover {
  color: #ffd600;
}

/* chart container */
.chart-container {
  position: relative;
}

.elevation-chart {
  height: 120px;
  padding: 0;
  cursor: pointer;
}

.chart-hint {
  padding:0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 0.5rem;

  .material-icons{
    font-size: 1.25em;
    cursor: pointer;
  }
}

.route-name-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-right: auto;
  margin-left: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

/* collapsed state */
.elevation-panel.collapsed {
  transform: translateX(-50%) translateY(100%);
  opacity: 0;
  pointer-events: none;
}

/* expand button */
.expand-button {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background: #1b1b1d;
  bottom: 1.8em;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  z-index: 499;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: background 0.2s;
}

.expand-button:hover {
  background: #2c2c2e;
}

.expand-button svg {
  flex-shrink: 0;
}

/* responsive */


@media (max-width: 768px) {
  .elevation-panel.collapsed {
    transform: translateY(100%);
  }

  .expand-button {
    left: 10px;
    transform: none;
  }
}
</style>
